/**
 * Admin Trial Management Service
 * Handles API calls for admin trial management operations
 */

import { apiClient } from './api.client';

// Types
export interface TrialAccount {
  id: string;
  manufacturerId: string;
  status: TrialStatus;
  startDate?: string;
  endDate?: string;
  budgetAllocated: number;
  budgetUsed: number;
  enabledBy?: string;
  enabledAt?: string;
  revokedBy?: string;
  revokedAt?: string;
  revokeReason?: string;
  createdAt: string;
  updatedAt: string;
  manufacturer: {
    id: string;
    name: string;
    contactEmail: string;
  };
}

export enum TrialStatus {
  PENDING_TRIAL_APPROVAL = 'PENDING_TRIAL_APPROVAL',
  TRIAL_ACTIVE = 'TRIAL_ACTIVE',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  FULL_REGISTRATION_PENDING_APPROVAL = 'FULL_REGISTRATION_PENDING_APPROVAL',
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED'
}

export interface TrialConfig {
  defaultBudget: number;
  defaultDuration: number;
  maxTrialsPerEmail: number;
  allowedOperations: string[];
  blockedOperations: string[];
}

export interface TrialAnalytics {
  totalTrials: number;
  activeTrials: number;
  expiredTrials: number;
  conversionRate: number;
  averageBudgetUsage: number;
  topOperations: Array<{
    operationType: string;
    count: number;
    totalCost: number;
  }>;
}

export interface TrialListResponse {
  accounts: TrialAccount[];
  total: number;
  page: number;
  totalPages: number;
}

export interface TrialFilters {
  status?: TrialStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AnalyticsFilters {
  startDate?: string;
  endDate?: string;
  status?: TrialStatus;
  manufacturerId?: string;
}

class AdminTrialManagementService {
  // Use relative path (no leading /api) because apiClient.baseURL already ends with /api
  private baseUrl = '/admin/trials';

  /**
   * Get all trial accounts with filtering and pagination
   */
  async getTrialAccounts(filters?: TrialFilters): Promise<TrialListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
    const payload = response.data?.data ?? response.data ?? {};

    return {
      accounts: payload.accounts ?? [],
      total: payload.total ?? 0,
      page: payload.page ?? filters?.page ?? 1,
      totalPages: payload.totalPages ?? 1
    };
  }

  /**
   * Get trial analytics
   */
  async getTrialAnalytics(filters?: AnalyticsFilters): Promise<TrialAnalytics> {
    const params = new URLSearchParams();
    
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.manufacturerId) params.append('manufacturerId', filters.manufacturerId);

    const response = await apiClient.get(`${this.baseUrl}/analytics?${params.toString()}`);
    return response.data;
  }

  /**
   * Get detailed trial account information
   */
  async getTrialAccountDetails(id: string): Promise<TrialAccount> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Activate a trial account
   */
  async activateTrial(id: string, config?: TrialConfig): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/activate`, { config });
  }

  /**
   * Revoke a trial account
   */
  async revokeTrial(id: string, reason: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/revoke`, { reason });
  }

  /**
   * Approve full registration for a trial account
   */
  async approveFullRegistration(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/approve-full-registration`);
  }

  /**
   * Reject full registration for a trial account
   */
  async rejectFullRegistration(id: string, reason: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/reject-full-registration`, { reason });
  }

  /**
   * Get trial accounts pending approval
   */
  async getPendingTrialApprovals(): Promise<TrialListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/pending-approval`);
    return response.data;
  }

  /**
   * Get trial accounts pending full registration approval
   */
  async getPendingFullRegistrationApprovals(): Promise<TrialListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/pending-full-registration`);
    return response.data;
  }

  /**
   * Get trial accounts expiring soon
   */
  async getExpiringSoonTrials(): Promise<TrialListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/expiring-soon`);
    return response.data;
  }

  /**
   * Manually trigger processing of expired trials
   */
  async processExpiredTrials(): Promise<{ processedCount: number }> {
    const response = await apiClient.post(`${this.baseUrl}/process-expired`);
    return response.data;
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: TrialStatus): string {
    switch (status) {
      case TrialStatus.PENDING_TRIAL_APPROVAL:
        return 'Pending Trial Approval';
      case TrialStatus.TRIAL_ACTIVE:
        return 'Trial Active';
      case TrialStatus.TRIAL_EXPIRED:
        return 'Trial Expired';
      case TrialStatus.FULL_REGISTRATION_PENDING_APPROVAL:
        return 'Full Registration Pending';
      case TrialStatus.ACTIVE:
        return 'Active (Full Account)';
      case TrialStatus.BLOCKED:
        return 'Blocked';
      default:
        return status;
    }
  }

  /**
   * Get status color class
   */
  getStatusColorClass(status: TrialStatus): string {
    switch (status) {
      case TrialStatus.PENDING_TRIAL_APPROVAL:
        return 'bg-yellow-100 text-yellow-800';
      case TrialStatus.TRIAL_ACTIVE:
        return 'bg-blue-100 text-blue-800';
      case TrialStatus.TRIAL_EXPIRED:
        return 'bg-gray-100 text-gray-800';
      case TrialStatus.FULL_REGISTRATION_PENDING_APPROVAL:
        return 'bg-purple-100 text-purple-800';
      case TrialStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case TrialStatus.BLOCKED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Calculate days remaining for a trial
   */
  calculateDaysRemaining(endDate?: string): number | null {
    if (!endDate) return null;
    
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  /**
   * Calculate budget usage percentage
   */
  calculateBudgetUsagePercentage(budgetUsed: number, budgetAllocated: number): number {
    if (budgetAllocated === 0) return 0;
    return Math.round((budgetUsed / budgetAllocated) * 100);
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
}

export const adminTrialManagementService = new AdminTrialManagementService();
